/**
 * How a chat's cloud browser was last shown: the tab on screen, the zoom, the
 * page's screen size (the device toolbar) and the colour scheme. The panel
 * remounts on every chat switch; it reads these back so returning to a chat
 * shows its browser as it was left. Kept for the page's life only, like the
 * browser panel's other view state; a reload starts from the defaults.
 */
import { scopedThreadKey } from "@amoreg/client-runtime/environment";
import {
  FILL_PREVIEW_VIEWPORT,
  type PreviewViewportSetting,
  type ScopedThreadRef,
} from "@amoreg/contracts";
import { useCallback } from "react";
import { create } from "zustand";

import type { CloudColorScheme } from "./components/preview/CloudBrowserMoreMenu";

export interface CloudBrowserViewSettings {
  /** The browser tab shown; null shows the agent's. */
  readonly shownTab: string | null;
  readonly zoom: number;
  readonly viewport: PreviewViewportSetting;
  /** The device toolbar's locked aspect ratio, while it has one. */
  readonly lockedAspectRatio: number | null;
  readonly colorScheme: CloudColorScheme;
}

export const DEFAULT_CLOUD_BROWSER_VIEW: CloudBrowserViewSettings = {
  shownTab: null,
  // Pixel-for-pixel with the panel (CloudBrowserView's CLOUD_ZOOM_DEFAULT).
  zoom: 1,
  viewport: FILL_PREVIEW_VIEWPORT,
  lockedAspectRatio: null,
  colorScheme: "system",
};

type SettingUpdate<V> = V | ((current: V) => V);

interface CloudBrowserViewStoreState {
  readonly byThreadKey: Record<string, CloudBrowserViewSettings>;
  readonly update: <K extends keyof CloudBrowserViewSettings>(
    threadKey: string,
    key: K,
    next: SettingUpdate<CloudBrowserViewSettings[K]>,
  ) => void;
}

export const useCloudBrowserViewStore = create<CloudBrowserViewStoreState>()((set) => ({
  byThreadKey: {},
  update: (threadKey, key, next) =>
    set((state) => {
      const current = state.byThreadKey[threadKey] ?? DEFAULT_CLOUD_BROWSER_VIEW;
      const value =
        typeof next === "function"
          ? (next as (value: CloudBrowserViewSettings[typeof key]) => CloudBrowserViewSettings[typeof key])(
              current[key],
            )
          : next;
      if (Object.is(value, current[key])) return state;
      return { byThreadKey: { ...state.byThreadKey, [threadKey]: { ...current, [key]: value } } };
    }),
}));

/**
 * One of a chat's browser view settings, as `useState` would give it: the
 * value, and a setter taking a value or an update of the current one.
 */
export function useCloudBrowserViewSetting<K extends keyof CloudBrowserViewSettings>(
  threadRef: ScopedThreadRef,
  key: K,
): readonly [
  CloudBrowserViewSettings[K],
  (next: SettingUpdate<CloudBrowserViewSettings[K]>) => void,
] {
  const threadKey = scopedThreadKey(threadRef);
  const value = useCloudBrowserViewStore(
    (state) => (state.byThreadKey[threadKey] ?? DEFAULT_CLOUD_BROWSER_VIEW)[key],
  );
  const setValue = useCallback(
    (next: SettingUpdate<CloudBrowserViewSettings[K]>) =>
      useCloudBrowserViewStore.getState().update(threadKey, key, next),
    [threadKey, key],
  );
  return [value, setValue] as const;
}
